import { useCallback } from 'react'

import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'

import DynamicForm from './DynamicForm'
import type { SchemaField } from './logic/schema'

const EMPTY_DEFAULT_VALUES = {}

export type CreateFormProps<T extends { [key: string]: any }> = {
  /** A generated *request* schema. Fields render from this, not from data. */
  schema: Array<StaticFieldSchema>
  onSubmit: (payload: Partial<T>) => Promise<void>
  /** Renders a Cancel button. Without it the form has no way out. */
  onCancel?: () => void
  defaultValues?: Partial<T>
  /** Last-mile reshape when the payload differs from the schema. */
  transformSubmit?: (values: Partial<T>) => Partial<T>
  submitText?: string
  fieldOverrides?: { [fieldPath: string]: SchemaField['component'] }
  selectOptions?: {
    [fieldPath: string]: Array<{
      label: string
      value: string
      sublabel?: string
    }>
  }
  arraySchemas?: { [fieldPath: string]: Array<StaticFieldSchema> }
  omitFields?: Array<string>
  fieldOrder?: Array<string>
  validationRules?: { [path: string]: any }
  requiredOverrides?: { [fieldPath: string]: boolean }
}

const CreateForm = <T extends { [key: string]: any }>({
  arraySchemas,
  defaultValues,
  fieldOrder,
  fieldOverrides,
  omitFields,
  onCancel,
  onSubmit,
  requiredOverrides,
  schema,
  selectOptions,
  submitText = 'Create',
  transformSubmit,
  validationRules,
}: CreateFormProps<T>) => {
  const handleSubmit = useCallback(
    async (payload: T | Partial<T>) => {
      const values = payload as Partial<T>
      await onSubmit(transformSubmit ? transformSubmit(values) : values)
    },
    [onSubmit, transformSubmit],
  )

  return (
    <DynamicForm<T>
      arraySchemas={arraySchemas}
      data={(defaultValues ?? EMPTY_DEFAULT_VALUES) as T}
      fieldEditMode='all'
      fieldOrder={fieldOrder}
      fieldOverrides={fieldOverrides}
      hideTabs
      // A create form with only optional fields is never dirty, and an empty
      // body is a legitimate request.
      ignoreIsDirty
      omitFields={omitFields}
      onCancel={onCancel}
      onSubmit={handleSubmit}
      requiredOverrides={requiredOverrides}
      schema={schema}
      selectOptions={selectOptions}
      submitText={submitText}
      validationRules={validationRules}
    />
  )
}

export default CreateForm
